/**
 * AR Analytics Tracking
 * Tracks user interactions with AR experiences and sends events to GA4
 */

(function() {
  "use strict";
  
  // Store GA4 measurement ID
  const GA4_ID = 'G-LMK5TKVN2J';
  
  // Session tracking flags and timing
  const sessions = {};
  
  // Check if GA is loaded, if not, initialize it
  function ensureGA4Loaded() {
    if (typeof gtag === 'undefined') {
      // Initialize gtag if not already available
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { dataLayer.push(arguments); };
      gtag('js', new Date());
      gtag('config', GA4_ID);
      
      // Load the GA script if not present
      if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
        document.head.appendChild(script);
      }
    }
  }
  
  // Main tracking function for AR events
  window.trackAREvent = function(eventName, productInfo, additionalParams = {}) {
    ensureGA4Loaded();
    
    // Combine product info with additional parameters
    const eventParams = {
      product_id: productInfo.id || '',
      variant_id: productInfo.variantId || '',
      product_title: productInfo.title || '',
      product_price: productInfo.price || '',
      model_url: productInfo.modelUrl || '',
      ...additionalParams
    };
    
    // Send to GA4
    gtag('event', eventName, eventParams);
    console.log(`[AR Analytics] ${eventName}`, eventParams);
  };
  
  // Initialize tracking on models when page loads
  document.addEventListener('DOMContentLoaded', function() {
    initARTracking();
  });
  
  // Track AR interactions for all model viewers on page
  function initARTracking() {
    // Find all model-viewer elements
    document.querySelectorAll('model-viewer').forEach(function(viewer) {
      setupModelViewerTracking(viewer);
    });
    
    // Track QR code interactions if they exist
    setupQRCodeTracking();
  }
  
  // Set up tracking for a specific model viewer
  function setupModelViewerTracking(modelViewer) {
    if (!modelViewer) return;
    
    // Get product data from context
    const productInfo = getProductInfoFromContext(modelViewer);
    const modelViewerId = modelViewer.id || 'unknown-model';
    
    // Track when AR button is clicked
    const arButton = modelViewer.querySelector('button[slot="ar-button"]');
    if (arButton) {
      arButton.addEventListener('click', function() {
        trackAREvent('ar_button_click', productInfo);
      });
    }
    
    // Listen for the ar-status event
    modelViewer.addEventListener('ar-status', function(event) {
      const status = event.detail.status;
      
      if (status === 'session-started') {
        // Track session start
        sessions[modelViewerId] = {
          startTime: Date.now(),
          productInfo: productInfo
        };
        
        trackAREvent('ar_session_started', productInfo, {
          placement: modelViewer.getAttribute('ar-placement') || 'unknown'
        });
      } else if (status === 'session-ended') {
        // Track session end and calculate duration
        if (sessions[modelViewerId] && sessions[modelViewerId].startTime) {
          const sessionDuration = Math.round((Date.now() - sessions[modelViewerId].startTime) / 1000);
          trackAREvent('ar_session_ended', productInfo, {
            session_duration_seconds: sessionDuration
          });
          
          // Clean up
          delete sessions[modelViewerId];
        } else {
          trackAREvent('ar_session_ended', productInfo);
        }
      } else if (status === 'failed') {
        trackAREvent('ar_session_failed', productInfo, {
          reason: event.detail.reason || 'unknown'
        });
      } else if (status === 'not-presenting') {
        // User exited AR mode
        if (sessions[modelViewerId] && sessions[modelViewerId].startTime) {
          const sessionDuration = Math.round((Date.now() - sessions[modelViewerId].startTime) / 1000);
          trackAREvent('ar_session_exited', productInfo, {
            session_duration_seconds: sessionDuration
          });
          
          // Clean up
          delete sessions[modelViewerId];
        }
      }
    });
    
    // Handle quick-look-button tap for iOS
    modelViewer.addEventListener('quick-look-button-tapped', function() {
      trackAREvent('ar_quick_look_tapped', productInfo);
    });
  }
  
  // Set up tracking for QR code interactions
  function setupQRCodeTracking() {
    // QR Code container
    const qrContainer = document.getElementById('ProductQRCode');
    if (!qrContainer) return;
    
    // Get product data
    const productInfo = getProductInfoFromPage();
    
    // Listen for custom event when QR code is generated
    document.addEventListener('qrcode:generated', function(event) {
      let detail = event.detail || {};
      trackAREvent('qr_code_generated', {
        ...productInfo,
        variantId: detail.variantId || productInfo.variantId
      });
    });
    
    // Track QR code info button clicks
    const infoBtn = qrContainer.querySelector('.qr-code-info-btn');
    if (infoBtn) {
      infoBtn.addEventListener('click', function() {
        trackAREvent('qr_info_toggled', productInfo, {
          action: infoBtn.classList.contains('active') ? 'collapse' : 'expand'
        });
      });
    }
    
    // Track minimize/maximize interactions
    const minimizeBtn = qrContainer.querySelector('.qr-minimize-btn');
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', function() {
        const isMinimized = qrContainer.classList.contains('minimized');
        trackAREvent('qr_visibility_changed', productInfo, {
          action: isMinimized ? 'minimize' : 'maximize'
        });
      });
    }
  }
  
  // Helper function to get product info from the page context
  function getProductInfoFromContext(element) {
    // Try to find product form
    const productForm = element.closest('form[action*="/cart/add"]');
    let productInfo = {};
    
    if (productForm) {
      const productInput = productForm.querySelector('input[name="id"]');
      const productJson = document.querySelector('[data-product-json]');
      
      if (productJson) {
        try {
          const productData = JSON.parse(productJson.textContent);
          productInfo = {
            id: productData.id,
            variantId: productInput ? productInput.value : '',
            title: productData.title,
            price: productData.price,
            modelUrl: element.getAttribute('src') || ''
          };
        } catch (e) {
          console.error('[AR Analytics] Error parsing product JSON', e);
        }
      } else {
        // Fallback to attributes on the model-viewer
        const modelViewerId = element.id || '';
        const variantId = modelViewerId.replace('model-viewer-', '');
        
        productInfo = {
          variantId: variantId,
          modelUrl: element.getAttribute('src') || ''
        };
      }
    }
    
    return productInfo;
  }
  
  // Get product info from the page
  function getProductInfoFromPage() {
    const productJson = document.querySelector('[data-product-json]');
    let productInfo = {};
    
    if (productJson) {
      try {
        const productData = JSON.parse(productJson.textContent);
        productInfo = {
          id: productData.id,
          title: productData.title,
          price: productData.price
        };
        
        // Try to get current variant
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput) {
          productInfo.variantId = variantInput.value;
        }
      } catch (e) {
        console.error('[AR Analytics] Error parsing product JSON', e);
      }
    }
    
    return productInfo;
  }
})();
